import { useState, useCallback, useEffect, useRef } from 'react';
import { SheetContent } from '@reflex/ui/components/ui/sheet';
import { cn } from '@reflex/ui/lib/utils';

interface ResizableSheetContentProps extends React.ComponentProps<typeof SheetContent> {
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  side?: 'left' | 'right';
  storageKey?: string;
}

function getStoredWidth(key: string, fallback: number): number {
  try {
    const v = localStorage.getItem(key);
    if (v) return Number(v);
  } catch {
    /* noop */
  }
  return fallback;
}

export function ResizableSheetContent({
  defaultWidth = 540,
  minWidth = 320,
  maxWidth = 1200,
  side = 'right',
  storageKey,
  className,
  children,
  ...props
}: ResizableSheetContentProps) {
  const [width, setWidth] = useState(() =>
    storageKey ? getStoredWidth(storageKey, defaultWidth) : defaultWidth,
  );
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startWidth.current = widthRef.current;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!dragging.current) return;
      const delta = startX.current - e.clientX;
      const direction = side === 'right' ? 1 : -1;
      const next = Math.min(maxWidth, Math.max(minWidth, startWidth.current + delta * direction));
      setWidth(next);
    }

    function onMouseUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, String(widthRef.current));
        } catch {
          /* noop */
        }
      }
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [maxWidth, minWidth, side, storageKey]);

  const handleEdge = side === 'right' ? 'left-0' : 'right-0';

  return (
    <SheetContent
      side={side}
      className={cn('p-0 sm:max-w-none', className)}
      // Cap the resolved width to the viewport so the panel never overflows on
      // narrow screens (the drag handle can request more than fits on mobile).
      style={{ width: `min(${width}px, 100vw)`, maxWidth: '100vw' }}
      {...props}
    >
      <div
        className={cn('absolute top-0 bottom-0 z-50 w-1.5 cursor-col-resize group', handleEdge)}
        onMouseDown={handleMouseDown}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panel"
      >
        <div
          className={cn(
            'absolute inset-y-0 w-px bg-border transition-colors',
            'group-hover:w-0.5 group-hover:bg-primary/50',
            side === 'right' ? 'left-0' : 'right-0',
          )}
        />
      </div>
      {children}
    </SheetContent>
  );
}
